<script lang="ts">
import { defineComponent } from 'vue';

interface PatientFlag {
  id: number;
  message: string;
  description: string | null;
  color: string;
  priority: number;
}

interface Insurance {
  payer: string | null;
  plan_name: string | null;
  group_id: string | null;
  member_id: string | null;
}

interface Patient {
  uuid: string;
  external_id?: string | number;
  name: string;
  chart_id: string | null;
  date_of_birth: string | null;
  phone: string | null;
  email: string | null;
  avatar_url: string;
  insurance: Insurance | null;
  flags: PatientFlag[] | null;
  weekly_appointment_count?: number;
  weekly_appointment_counts_by_office?: WeeklyAppointmentCount[];
  last_7_days_appointment_count?: number;
  last_7_days_appointment_counts_by_office?: WeeklyAppointmentCount[];
  last_year_appointment_count?: number;
  last_year_appointment_counts_by_office?: WeeklyAppointmentCount[];
  patient_appointment_counts?: Record<PatientAppointmentPeriodKey, number>;
  patient_appointment_counts_by_discipline?: Record<PatientAppointmentPeriodKey, DisciplineAppointmentCount[]>;
  future_visits?: FutureVisit[];
  future_visits_remaining_count?: number;
}

interface WeeklyAppointmentCount {
  office_id: number;
  office_uuid: string;
  office_name: string;
  appointments_count: number;
}

type PatientAppointmentPeriodKey = 'this_week' | 'last_week' | 'this_year';

interface DisciplineAppointmentCount {
  discipline: string;
  appointments_count: number;
}

interface PatientAppointmentPeriod {
  key: PatientAppointmentPeriodKey;
  label: string;
  total: number;
  disciplines: DisciplineAppointmentCount[];
}

interface FutureVisit {
  uuid: string;
  starts_at: string | null;
  starts_at_display?: string | null;
  doctor: string | null;
  location: string | null;
  procedure: string | null;
}

export default defineComponent({
  name: 'PatientSearch',
  data() {
    return {
      searchQuery: '',
      patients: [] as Patient[],
      selectedPatient: null as Patient | null,
      pendingPatient: null as Patient | null,
      showAlertDialog: false,
      isSearching: false,
      searchTimeout: null as any,
      hasSearched: false,
    };
  },
  computed: {
    alertFlags(): PatientFlag[] {
      return this.pendingPatient ? this.patientAlertFlags(this.pendingPatient) : [];
    },
  },
  methods: {
    async searchPatients() {
      if (this.searchQuery.length < 2) {
        this.patients = [];
        this.hasSearched = false;
        return;
      }

      this.isSearching = true;
      this.hasSearched = false;
      try {
        const response = await this.$api.customRequest({
          method: 'post',
          url: 'scheduler/search-patients',
          data: {
            query: this.searchQuery,
            current_office_uuid: this.currentOfficeUuid(),
          },
        });

        this.patients = response.data?.patients || response.data || response || [];
        this.hasSearched = true;
      } catch (e) {
        console.error('SEARCH_PATIENTS ERROR - ', e);
        this.patients = [];
        this.hasSearched = true;
      } finally {
        this.isSearching = false;
      }
    },
    onSearchInput() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchPatients();
      }, 300);
    },
    selectPatient(patient: Patient) {
      this.patients = [];
      this.hasSearched = false;
      this.searchQuery = patient.name;

      if (this.patientAlertFlags(patient).length > 0) {
        this.pendingPatient = patient;
        this.showAlertDialog = true;
        return;
      }

      this.commitPatientSelection(patient);
    },
    commitPatientSelection(patient: Patient) {
      this.selectedPatient = patient;
      this.$emit('patient-selected', patient);
      this.pendingPatient = null;
      this.showAlertDialog = false;
    },
    confirmAlertFlags() {
      if (this.pendingPatient) {
        this.commitPatientSelection(this.pendingPatient);
      }
    },
    clearSelection() {
      this.selectedPatient = null;
      this.pendingPatient = null;
      this.showAlertDialog = false;
      this.searchQuery = '';
      this.patients = [];
      this.hasSearched = false;
      this.$emit('patient-cleared');
    },
    patientAlertFlags(patient: Patient): PatientFlag[] {
      return (patient.flags || []).filter((flag) => flag.message?.toLowerCase().includes('alert'));
    },
    flagDisplayText(flag: PatientFlag): string {
      return flag.description ? `${flag.message}: ${flag.description}` : flag.message;
    },
    getFlagStyle(flag: PatientFlag) {
      return {
        backgroundColor: flag.color || '#CCCCCC',
      };
    },
    getFlagBorderStyle(flag: PatientFlag) {
      return {
        borderColor: flag.color || '#eeeeee',
      };
    },
    patientAppointmentPeriods(patient: Patient): PatientAppointmentPeriod[] {
      const labels: Record<PatientAppointmentPeriodKey, string> = {
        this_week: 'This Week',
        last_week: 'Last Week',
        this_year: 'This Year',
      };

      return (Object.keys(labels) as PatientAppointmentPeriodKey[]).map((key) => {
        const disciplines = patient.patient_appointment_counts_by_discipline?.[key] || [];
        const derivedTotal = disciplines.reduce(
          (total, discipline) => total + Number(discipline.appointments_count || 0),
          0,
        );

        return {
          key,
          label: labels[key],
          total: typeof patient.patient_appointment_counts?.[key] === 'number'
            ? Number(patient.patient_appointment_counts[key])
            : derivedTotal,
          disciplines,
        };
      });
    },
    patientAppointmentSummary(patient: Patient): string {
      const thisWeek = this.patientAppointmentPeriods(patient).find((period) => period.key === 'this_week');
      const total = thisWeek?.total || 0;
      const suffix = total === 1 ? 'patient appointment' : 'patient appointments';

      return total > 0 ? `${total} ${suffix} this week` : 'No patient appointments this week';
    },
    thisWeekPatientAppointmentTotal(patient: Patient): number {
      return this.patientAppointmentPeriods(patient).find((period) => period.key === 'this_week')?.total || 0;
    },
    weeklyAppointmentCounts(patient: Patient): WeeklyAppointmentCount[] {
      return patient.last_7_days_appointment_counts_by_office || patient.weekly_appointment_counts_by_office || [];
    },
    weeklyAppointmentTotal(patient: Patient): number {
      if (typeof patient.last_7_days_appointment_count === 'number') {
        return patient.last_7_days_appointment_count;
      }

      if (typeof patient.weekly_appointment_count === 'number') {
        return patient.weekly_appointment_count;
      }

      return this.weeklyAppointmentCounts(patient).reduce(
        (total, office) => total + Number(office.appointments_count || 0),
        0,
      );
    },
    weeklyAppointmentLabel(patient: Patient): string {
      const total = this.weeklyAppointmentTotal(patient);
      const suffix = total === 1 ? 'appointment' : 'appointments';

      return total > 0 ? `${total} ${suffix} in last 7 days` : 'No appointments in last 7 days';
    },
    yearlyAppointmentCounts(patient: Patient): WeeklyAppointmentCount[] {
      return patient.last_year_appointment_counts_by_office || [];
    },
    yearlyAppointmentTotal(patient: Patient): number {
      if (typeof patient.last_year_appointment_count === 'number') {
        return patient.last_year_appointment_count;
      }

      return this.yearlyAppointmentCounts(patient).reduce(
        (total, office) => total + Number(office.appointments_count || 0),
        0,
      );
    },
    yearlyAppointmentLabel(patient: Patient): string {
      const total = this.yearlyAppointmentTotal(patient);
      const suffix = total === 1 ? 'appointment' : 'appointments';

      return total > 0 ? `${total} ${suffix} in last year` : 'No appointments in last year';
    },
    currentOfficeUuid(): string | null {
      return this.$store.state.scheduler?.currentOfficeUuid || this.$store.state.auth?.office || null;
    },
    formatFutureVisitDate(visit: FutureVisit): string {
      if (visit.starts_at_display) {
        return visit.starts_at_display;
      }

      if (!visit.starts_at) {
        return 'Date not available';
      }

      return this.$dayjs(visit.starts_at).format('MMM D, YYYY h:mm A');
    },
    futureVisitsRemainingText(patient: Patient): string {
      const remaining = patient.future_visits_remaining_count || 0;
      const suffix = remaining === 1 ? 'visit' : 'visits';

      return `and ${remaining} more ${suffix}`;
    },
  },
});
</script>

<template>
  <div class="patientSearchContainer">
    <h2 class="searchTitle my-2">Patient Finder</h2>
    <p class="searchSubtitle mb-3">Search patient by Name, Date of Birth, Phone number, or Chart ID</p>

    <div class="searchInputWrapper">
      <v-text-field
        v-model="searchQuery"
        label="Search Patient"
        placeholder="Type patient name, phone, DOB, or chart ID"
        variant="outlined"
        density="compact"
        clearable
        :loading="isSearching"
        @input="onSearchInput"
        @keydown.escape="clearSelection"
        class="patientSearchInput"
      ></v-text-field>

      <v-list v-if="patients.length > 0" class="patientsList elevation-1">
        <v-list-item
          v-for="patient in patients"
          :key="patient.uuid"
          @click="selectPatient(patient)"
          class="patientItem"
        >
          <template #prepend>
            <v-avatar size="40" class="mr-3">
              <v-img :src="patient.avatar_url" :alt="patient.name">
                <template #placeholder>
                  <v-icon>mdi-account-circle</v-icon>
                </template>
              </v-img>
            </v-avatar>
          </template>
          <v-list-item-title class="patientName">{{ patient.name }}</v-list-item-title>
          <v-list-item-subtitle class="patientInfo">
            <span v-if="patient.chart_id" class="mr-2">Chart ID: {{ patient.chart_id }}</span>
            <span v-if="patient.phone" class="mr-2">Phone: {{ patient.phone }}</span>
            <span v-if="patient.date_of_birth">DOB: {{ patient.date_of_birth }}</span>
            <span v-if="patient.email">Email: {{ patient.email }}</span>
          </v-list-item-subtitle>
          <div class="weeklyAppointmentsCompact" :class="{ isEmpty: thisWeekPatientAppointmentTotal(patient) === 0 }">
            <v-icon size="small" class="mr-1">mdi-calendar-week</v-icon>
            <span>{{ patientAppointmentSummary(patient) }}</span>
          </div>
        </v-list-item>
      </v-list>

      <div v-if="hasSearched && patients.length === 0 && !isSearching" class="noResultsMessage">
        <v-icon color="#999">mdi-account-search</v-icon>
        <p>No patients found matching your search</p>
      </div>
    </div>

    <div v-if="selectedPatient" class="selectedPatientCard mt-3 elevation-1">
      <div class="patientCardHeader">
        <div class="flex justify-between items-center">
          <div class="flex items-center flex-1">
            <v-avatar size="48" class="mr-2">
              <v-img :src="selectedPatient.avatar_url" :alt="selectedPatient.name">
                <template #placeholder>
                  <v-icon size="48">mdi-account-circle</v-icon>
                </template>
              </v-img>
            </v-avatar>
            <div class="flex-1">
              <h3 class="selectedPatientName">{{ selectedPatient.name }}</h3>
              <div class="patientDetailsCompact">
                <span v-if="selectedPatient.chart_id" class="detailItem">ID: {{ selectedPatient.chart_id }}</span>
                <span v-if="selectedPatient.date_of_birth" class="detailItem">DOB: {{ selectedPatient.date_of_birth }}</span>
                <span v-if="selectedPatient.phone" class="detailItem">{{ selectedPatient.phone }}</span>
                <span v-if="selectedPatient.email" class="detailItem">{{ selectedPatient.email }}</span>
              </div>
            </div>
          </div>
          <v-btn
            icon
            size="x-small"
            @click="clearSelection"
            class="closeBtn"
          >
            <v-icon size="small">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <div class="patientCardBody">
        <div class="weeklyAppointmentsSection">
          <div class="sectionHeader">
            <v-icon size="small" class="mr-1">mdi-chart-box</v-icon>
            <span class="sectionTitle">Patient Appointments by Discipline</span>
          </div>
          <div class="patientStatsGrid">
            <div
              v-for="period in patientAppointmentPeriods(selectedPatient)"
              :key="period.key"
              class="patientStatsPeriod"
              :class="{ isEmpty: period.total === 0 }"
            >
              <div class="patientStatsHeader">
                <span>{{ period.label }}</span>
                <strong>{{ period.total }}</strong>
              </div>
              <div v-if="period.disciplines.length > 0" class="disciplineCountsList">
                <span
                  v-for="discipline in period.disciplines"
                  :key="`${period.key}-${discipline.discipline}`"
                  class="disciplineCountChip"
                >
                  {{ discipline.discipline }}: {{ discipline.appointments_count }}
                </span>
              </div>
              <div v-else class="emptyStatsText">No patient appointments</div>
            </div>
          </div>
        </div>

        <div v-if="(selectedPatient.future_visits || []).length > 0" class="futureVisitsSection">
          <div class="sectionHeader">
            <v-icon size="small" class="mr-1">mdi-calendar-clock</v-icon>
            <span class="sectionTitle">Upcoming Visits</span>
          </div>
          <div class="futureVisitsList">
            <div
              v-for="visit in selectedPatient.future_visits"
              :key="visit.uuid"
              class="futureVisitItem"
            >
              <div class="futureVisitDate">{{ formatFutureVisitDate(visit) }}</div>
              <div class="futureVisitMeta">
                <span v-if="visit.doctor">{{ visit.doctor }}</span>
                <span v-if="visit.location">{{ visit.location }}</span>
                <span v-if="visit.procedure">{{ visit.procedure }}</span>
              </div>
            </div>
            <div v-if="(selectedPatient.future_visits_remaining_count || 0) > 0" class="futureVisitsMore">
              {{ futureVisitsRemainingText(selectedPatient) }}
            </div>
          </div>
        </div>

        <div v-if="selectedPatient.insurance && (selectedPatient.insurance.payer || selectedPatient.insurance.plan_name || selectedPatient.insurance.member_id || selectedPatient.email)" class="insuranceSection">
          <div class="sectionHeader">
            <v-icon size="small" class="mr-1">mdi-shield-check</v-icon>
            <span class="sectionTitle">Insurance</span>
          </div>
          <div class="insuranceDetailsCompact">
            <div v-if="selectedPatient.insurance.payer || selectedPatient.insurance.plan_name">
              <span v-if="selectedPatient.insurance.payer">{{ selectedPatient.insurance.payer }}</span>
              <span v-if="selectedPatient.insurance.plan_name"> - {{ selectedPatient.insurance.plan_name }}</span>
            </div>
            <div v-if="selectedPatient.insurance.member_id">
              Member ID: {{ selectedPatient.insurance.member_id }}
            </div>
            <div v-if="selectedPatient.email">
              Email: {{ selectedPatient.email }}
            </div>
          </div>
        </div>

        <div v-if="(selectedPatient.flags || []).length > 0" class="flagsSection">
          <div class="sectionHeader">
            <v-icon size="small" class="mr-1">mdi-flag</v-icon>
            <span class="sectionTitle">Flags</span>
          </div>
          <div class="flagsDetailList">
            <div
              v-for="flag in selectedPatient.flags"
              :key="flag.id"
              class="flagDetailItem"
              :style="getFlagBorderStyle(flag)"
            >
              <span class="flagDetailLabel" :style="getFlagStyle(flag)">
                {{ flag.message }}
              </span>
              <span v-if="flag.description" class="flagDetailDescription" :style="getFlagBorderStyle(flag)">
                {{ flag.description }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="showAlertDialog" persistent max-width="520">
      <v-card class="alertDialog">
        <v-card-title class="alertDialogTitle">
          <v-icon color="#d94554" class="mr-2">mdi-alert-circle</v-icon>
          Patient Alert
        </v-card-title>
        <v-card-text>
          <div v-if="pendingPatient" class="alertPatientName">{{ pendingPatient.name }}</div>
          <div class="alertFlagsList">
            <div
              v-for="flag in alertFlags"
              :key="`alert-flag-${flag.id}`"
              class="alertFlagItem"
              :style="getFlagBorderStyle(flag)"
            >
              <span class="alertFlagLabel" :style="getFlagStyle(flag)">{{ flag.message }}</span>
              <p v-if="flag.description" class="alertFlagDescription">{{ flag.description }}</p>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="alertDialogActions">
          <v-btn color="primary" variant="flat" @click="confirmAlertFlags">
            Close and Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.patientSearchContainer {
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
}

.searchTitle {
  font-size: 18px;
  font-weight: 700;
  color: #111111;
}

.searchSubtitle {
  font-size: 13px;
  color: #666666;
}

.searchInputWrapper {
  position: relative;
  width: 100%;
  overflow: visible;
  z-index: 101;
}

.patientSearchInput :deep(.v-field) {
  background: transparent;
}

.patientsList {
  position: absolute;
  top: 70%;
  left: 0;
  right: 0;
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 4px;
  margin-top: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: white;
}

:deep(.patientsList .v-list) {
  max-height: 100%;
}

.noResultsMessage {
  text-align: center;
  padding: 20px;
  color: #999999;
  background: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  margin-top: 8px;
}

.noResultsMessage p {
  margin: 8px 0 0 0;
  font-size: 14px;
}

.patientItem {
  cursor: pointer;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0;
}

.patientItem:hover {
  background: #f5f5f5;
}

.patientName {
  font-weight: 600;
  color: #111111;
  font-size: 14px;
}

.patientInfo {
  font-size: 12px;
  color: #666666;
}

.weeklyAppointmentsCompact {
  display: flex;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #086397;
}

.weeklyAppointmentsCompact.isEmpty,
.weeklyAppointmentTotal.isEmpty {
  color: #777777;
  font-weight: 500;
}

.officeCountsList {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.officeCountChip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eef6fb;
  color: #234f65;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}

.selectedPatientCard {
  background: white;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  padding: 12px;
}

.patientCardHeader {
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.selectedPatientName {
  font-size: 15px;
  font-weight: 700;
  color: #111111;
  margin-bottom: 2px;
}

.patientDetailsCompact {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 11px;
  color: #666666;
}

.detailItem {
  white-space: nowrap;
}

.patientCardBody {
  padding-top: 10px;
}

.weeklyAppointmentsSection {
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.futureVisitsSection {
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.futureVisitsList {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 22px;
}

.futureVisitItem {
  padding: 8px;
  border: 1px solid #d9e7ef;
  border-radius: 6px;
  background: #f7fbfd;
}

.futureVisitDate {
  font-size: 12px;
  font-weight: 700;
  color: #111111;
  line-height: 16px;
}

.futureVisitMeta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 4px;
  color: #555555;
  font-size: 11px;
  line-height: 16px;
}

.futureVisitMeta span:not(:last-child)::after {
  content: "•";
  margin-left: 8px;
  color: #999999;
}

.futureVisitsMore {
  color: #086397;
  font-size: 12px;
  font-weight: 700;
}

.weeklyAppointmentDetails {
  padding-left: 22px;
}

.weeklyAppointmentTotal {
  font-size: 12px;
  font-weight: 700;
  color: #086397;
}

.selectedOfficeCounts {
  margin-top: 6px;
}

.patientStatsGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding-left: 22px;
}

.patientStatsPeriod {
  min-width: 0;
  padding: 8px;
  border: 1px solid #d9e7ef;
  border-radius: 6px;
  background: #f7fbfd;
}

.patientStatsPeriod.isEmpty {
  background: #fafafa;
  border-color: #eeeeee;
}

.patientStatsHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #111111;
}

.patientStatsHeader strong {
  color: #086397;
}

.disciplineCountsList {
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
  margin-top: 8px;
}

.disciplineCountChip {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  padding: 2px 6px;
  border-radius: 4px;
  background: #eef6fb;
  color: #234f65;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
}

.emptyStatsText {
  margin-top: 8px;
  color: #777777;
  font-size: 11px;
  line-height: 16px;
}

.insuranceSection {
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.sectionHeader {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.sectionTitle {
  font-size: 11px;
  font-weight: 700;
  color: #111111;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insuranceDetailsCompact {
  font-size: 12px;
  color: #666666;
  padding-left: 22px;
  line-height: 18px;
}

.flagsSection {
  padding-top: 4px;
}

.flagsDetailList {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 22px;
}

.flagDetailItem {
  display: grid;
  grid-template-columns: minmax(110px, max-content) 1fr;
  gap: 8px;
  align-items: start;
}

.flagDetailLabel {
  display: inline-flex;
  align-items: center;
  max-width: 180px;
  padding: 4px 8px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  cursor: default;
  overflow: hidden;
  text-overflow: ellipsis;
}

.flagDetailDescription {
  padding: 6px 8px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  font-size: 12px;
  line-height: 17px;
  color: #555555;
  overflow-wrap: anywhere;
}

@media (max-width: 520px) {
  .flagDetailItem {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .patientStatsGrid {
    grid-template-columns: 1fr;
  }
}

.alertDialog {
  border-radius: 8px;
}

.alertDialogTitle {
  display: flex;
  align-items: center;
  color: #111111;
  font-size: 18px;
  font-weight: 700;
}

.alertPatientName {
  font-size: 15px;
  font-weight: 700;
  color: #111111;
  margin-bottom: 12px;
}

.alertFlagsList {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alertFlagItem {
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 10px;
  background: #fafafa;
}

.alertFlagLabel {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 3px;
  color: white;
  font-size: 11px;
  font-weight: 700;
}

.alertFlagDescription {
  margin: 8px 0 0;
  color: #444444;
  font-size: 13px;
  line-height: 18px;
}

.alertDialogActions {
  padding: 12px 24px 20px;
  justify-content: flex-end;
}

.closeBtn {
  margin-top: -4px;
}
</style>
